import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { consumeRedirectAfterLogin } from "../../utils/auth";
import styles from "./authForm.module.css";

export default function Login() {
  const baseURL = process.env.REACT_APP_BACKEND_API_URL;
  if (!baseURL) {
    throw new Error(
      "Missing REACT_APP_BACKEND_API_URL. Set it in Coolify before building."
    );
  }

  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${baseURL}api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));

      const errMsg = data.detail ?? data.error ?? data.message;
      if (errMsg) {
        if (
          String(errMsg).toLowerCase().includes("not confirmed") ||
          errMsg === "User is not confirmed."
        ) {
          navigate("/confirm", { state: { email } });
          return;
        }
        setError(typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg));
        return;
      }

      if (!res.ok) {
        setError("Login failed. Please try again.");
        return;
      }

      if (!data.IdToken) {
        setError("Invalid response from server.");
        return;
      }

      const decoded: any = jwtDecode(data.IdToken);
      const groups = decoded["cognito:groups"] || [];

      localStorage.setItem("idToken", data.IdToken);
      localStorage.setItem("accessToken", data.AccessToken);
      localStorage.setItem("tokenExpiry", data.ExpiresIn);

      // A page that bounced the user here (the cart, for instance) leaves the
      // path it wants them returned to. Consume it so it can't leak into a
      // later, unrelated login.
      const redirectAfterLogin = consumeRedirectAfterLogin();
      if (redirectAfterLogin) {
        navigate(redirectAfterLogin);
      } else if (groups.includes("admin")) {
        navigate("/admin");
      } else if (groups.includes("Provider") || groups.includes("provider")) {
        navigate("/dashboard");
      } else if (groups.includes("Patient") || groups.includes("patient")) {
        navigate("/patient-dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (err: any) {
      let msg =
        err.message ||
        (err.cause?.message ?? "Network error. Is the backend running?");
      if (
        typeof baseURL === "string" &&
        baseURL.startsWith("http:") &&
        typeof window !== "undefined" &&
        window.location?.protocol === "https:"
      ) {
        msg =
          "Request blocked (mixed content). Use an HTTPS backend URL when the app is on HTTPS.";
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.pane}>
      <header className={styles.head}>
        <h2 className={styles.title}>Welcome back</h2>
        <p className={styles.lead}>
          Log in to reach your sessions, orders, and dashboards.
        </p>
      </header>

      <form onSubmit={handleLogin} className={styles.form} noValidate>
        {error && (
          <p className="ui-banner ui-banner--error" role="alert">
            {error}
          </p>
        )}

        <div className="ui-field">
          <label className="ui-label" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            className="ui-input"
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="ui-field">
          <label className="ui-label" htmlFor="password">
            Password
          </label>
          <div className={styles.secretField}>
            <input
              id="password"
              className={`ui-input ${styles.secretInput}`}
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className={styles.reveal}
              aria-label={showPassword ? "Hide password" : "Show password"}
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        <button
          type="submit"
          className={`ui-btn ui-btn--lg ui-btn--full ${styles.submit}`}
          disabled={loading || !email || !password}
          aria-busy={loading}
        >
          {loading ? "Logging in..." : "Log in"}
        </button>
      </form>

      <div className={styles.links}>
        <p>
          New to mRehab?{" "}
          <Link to="/signup" className="ui-link">
            Create an account
          </Link>
        </p>
        <p>
          <Link to="/forgot-password" className="ui-link">
            Forgot your password?
          </Link>
        </p>
      </div>
    </div>
  );
}
